import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardMeta,
	Label,
	Loader,
	Tab
} from 'semantic-ui-react';

import { log, offsetTimezone } from '../../../../../../services/utils';

import {
	overviewSearchBySubmission,
	overviewSearchByAgency
} from '../../../../../../api/nora/programs';
import { getDocument } from '../../../../../../api/nora/submissions';

import { useProgramState } from '../../../../../../contexts/ProgramsContext';
import { useSubmissionAction, useSubmissionState } from '../../../../../../contexts/SubmissionsContext';
import { useUIAction } from '../../../../../../contexts/UIContext';

import Kanban from '../../../../../Kanban';
import Actions from '../../../../../Shared/Actions';
import PageHeader from '../../../../../Shared/PageHeader';
import HelpModal from '../../../../../Shared/HelpModal';
import SubmissionSearchModal from './SubmissionSearchModal';
import AgencySearchModal from './AgencySearchModal';
import DocumentModal from '../../../../../DocumentModal';

const OverviewTab = () => {
	const {
		override,
		program
	} = useProgramState();
	const { document } = useSubmissionState();
	const { setDocument } = useSubmissionAction();
	const { setApiErrorAlert, setScreenLoader } = useUIAction();

	const [ data, setData ] = useState( [] );
	const [ lastSearch, setLastSearch ] = useState( { type: 'default', param: null } );
	const [ filter, setFilter ] = useState( '' );
	const [ submissionSearchModal, setSubmissionSearchModal ] = useState( false );
	const [ agencySearchModal, setAgencySearchModal ] = useState( false );

	const submissionOverview = useCallback( () => {
		setSubmissionSearchModal( true );
	}, [] );

	const agencyOverview = useCallback( () => {
		setAgencySearchModal( true );
	}, [] );

	const overviewActions = useMemo( () => [
		{
			action: submissionOverview,
			disable: false,
			icon: 'filter',
			permission: true,
			text: 'Filter By Submission'
		},
		{
			action: agencyOverview,
			disable: false,
			icon: 'filter',
			permission: true,
			text: 'Filter By Agency'
		}
	], [
		agencyOverview,
		submissionOverview
	] );

	const mapOverviewResponse = useCallback( response => response.map( ( { type, records } ) => ( {
		column: { title: type.name },
		cards: records.map( record => ( {
			agency: record.name,
			date: offsetTimezone( `${ record.statusDate }`, 'date' ),
			route: record.route,
			submission: record.title,
			submissionId: record.route.navigationState.submissionID
		} ) )
	} ) ), [] );

	const searchDefault = useCallback( async () => {
		try {
			const response = await overviewSearchBySubmission( program.id, 0 );

			const mappedResponse = mapOverviewResponse( response );

			setLastSearch( { type: 'default', param: null } );
			setData( mappedResponse );
		} catch ( error ) {
			setApiErrorAlert( error );
		}
	}, [
		mapOverviewResponse,
		program.id,
		setApiErrorAlert
	] );

	useEffect( () => {
		searchDefault();
		setFilter( '' );
	}, [ searchDefault ] );

	const searchBySubmission = useCallback( async instanceId => {
		try {
			const response = await overviewSearchBySubmission( program.id, instanceId );

			const mappedResponse = mapOverviewResponse( response );

			setLastSearch( { type: 'submission', param: instanceId } );
			setData( mappedResponse );
		} catch ( error ) {
			setApiErrorAlert( error );
		}
	}, [
		mapOverviewResponse,
		program.id,
		setApiErrorAlert
	] );

	const closeSubmissionSearchModal = useCallback( () => {
		setSubmissionSearchModal( false );
	}, [] );

	const searchByAgency = useCallback( async agencyId => {
		try {
			const response = await overviewSearchByAgency( program.id, agencyId );

			const mappedResponse = mapOverviewResponse( response );

			setLastSearch( { type: 'agency', param: agencyId } );
			setData( mappedResponse );
		} catch ( error ) {
			setApiErrorAlert( error );
		}
	}, [
		mapOverviewResponse,
		program.id,
		setApiErrorAlert
	] );

	const retrieveDocument = useCallback( async id => {
		try {
			setScreenLoader( 'Retrieving Submission...' );
			const document = await getDocument( id );

			setDocument( document );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setScreenLoader( '' );
	}, [
		setApiErrorAlert,
		setDocument,
		setScreenLoader
	] );

	const mapCards = useCallback( cards => cards.map( card => {
		const handleCardClick = () => {
			retrieveDocument( card.route.navigationState.submissionID );
		};

		return (
			<Card
				key={ card.submissionId }
				onClick={ handleCardClick }
			>
				<CardContent>
					<CardHeader style={ { fontSize: '1rem' } }>{ card.agency }</CardHeader>
					<CardMeta style={ { fontSize: '0.9rem' } }>{ card.date }</CardMeta>
					<CardDescription>{ card.submission }</CardDescription>
				</CardContent>
			</Card>
		);
	} ), [ retrieveDocument ] );

	const closeAgencySearchModal = useCallback( () => {
		setAgencySearchModal( false );
	}, [] );

	const closeDocumentModal = useCallback( () => {
		setDocument( null );

		switch ( lastSearch.type ) {
			case 'submission': {
				searchBySubmission( lastSearch.param );
				break;
			}

			case 'agency': {
				searchByAgency( lastSearch.param );
				break;
			}

			default: {
				searchDefault();
			}
		}
	}, [ lastSearch.param, lastSearch.type, searchByAgency, searchBySubmission, searchDefault, setDocument ] );

	const handleClearFilter = useCallback( async () => {
		setData( [] );
		await searchDefault();
		setFilter( '' );
	}, [ searchDefault ] );

	return (
		<Tab.Pane as="div">
			{ log( 'overviewTab' ) }
			<Actions actions={ [] } />
			<PageHeader
				actions={ overviewActions }
				title={
					<>
						<span>Overview</span>
						<HelpModal
							id={ override?.id === 'onboarding' ? 38 : 10 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</>
				}
			/>
			{ data.length
				? <>
					{ filter
						? <p>
							{ filter }&nbsp;&nbsp;
							<Label
								basic
								content="Clear Filter"
								icon="times"
								style={ { cursor: 'pointer' } }
								onClick={ handleClearFilter }
								size="tiny"
							/>
						</p>
						: <p>Showing the status of all submissions from all agencies.</p>
					}
					<Kanban
						data={ data }
						mapCards={ mapCards }
					/>
				</>
				: <Loader active />
			}
			<SubmissionSearchModal
				closeModal={ closeSubmissionSearchModal }
				onSearch={ searchBySubmission }
				open={ submissionSearchModal }
				setFilter={ setFilter }
			/>
			<AgencySearchModal
				closeModal={ closeAgencySearchModal }
				onSearch={ searchByAgency }
				open={ agencySearchModal }
				setFilter={ setFilter }
			/>
			<DocumentModal
				closeModal={ closeDocumentModal }
				open={ document }
			/>
		</Tab.Pane>
	);
};

export default memo( OverviewTab );
